import { CloudUpload, Download, FileCheck2, FileWarning, Loader2 } from 'lucide-react';
import {
  DAILY_REPORT_STATUS_LABEL,
  describeDailyReport,
  isRenderable,
  type DailyReport,
  type DailyReportStatus,
} from '@obraplan/core/domain/planning/daily-report';
import { projectTerm } from '@obraplan/core/domain/planning/daily-report-term';
import type { Project } from '@obraplan/core/domain/structure/project';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

/**
 * O cabeçalho do documento — a obra vista de cima, e o estado do RDO do dia.
 *
 * ```text
 * RDO nº 142 · rev. 2                                    [ Emitido ]
 * SPE Batista de Castro · Vienna Residencial
 * Av. Independência, 1200 — Sorocaba/SP
 * Responsável técnico: Douglas Arvelos
 * Prazo 1095 dias   ·   Decorrido 678 dias   ·   Saldo 417 dias
 * ```
 *
 * Os três números do prazo **ninguém digita**: saem do início e do término previsto da
 * obra (`daily-report-term.ts`). Obra sem essas datas simplesmente não mostra a faixa —
 * inventar um prazo a partir da data que existe daria um saldo que ninguém combinou.
 *
 * ## Os quatro estados do botão, e por que o primeiro existe
 *
 * ```text
 *   sem relatório  →  Iniciar o relatório do dia   (cria o rascunho, herda o equipamento)
 *   RASCUNHO       →  Emitir o RDO                 (atribui número — RN-209)
 *   EMITIDO        →  Abrir revisão                (labor.modify, como reabrir conferência)
 *   DESATUALIZADO  →  Emitir a revisão nº N        (o dia mudou por baixo — RN-210)
 * ```
 *
 * O primeiro é o que a fatia 6 acrescentou ao desenho. Ele podia não existir — toda
 * escrita de conteúdo abre o rascunho sozinha —, e existe por causa do equipamento: ele é
 * herdado do último RDO emitido no instante em que o documento nasce (ADR-123), e sem um
 * gesto para começar o dia a lista herdada só apareceria depois da primeira gravação.
 * Quem relata começaria olhando uma seção vazia que o sistema já sabia preencher.
 *
 * ## E o PDF, que só aparece quando o documento está valendo
 *
 * `isRenderable` é a RN-217: só o emitido **e em dia** vira arquivo. O rascunho não tem
 * número, e o desatualizado tem um que mente — sairia um papel com o cabeçalho da emissão
 * anterior e o conteúdo de agora. Nos dois casos o botão não some em silêncio: ele dá
 * lugar à frase que diz o que falta fazer, que é sempre um toque no botão ao lado.
 */

const TOM: Readonly<Record<DailyReportStatus, 'neutral' | 'ok' | 'warn'>> = {
  DRAFT: 'neutral',
  CLOSED: 'ok',
  OUTDATED: 'warn',
};

function dias(quantidade: number): string {
  return `${String(quantidade)} ${Math.abs(quantidade) === 1 ? 'dia' : 'dias'}`;
}

export function DailyReportHeader({
  project,
  date,
  report,
  canConfirm,
  canModify,
  isPending,
  emissionPending = false,
  isGeneratingPdf,
  onClose,
  onRevise,
  onDownloadPdf,
}: {
  project: Project | undefined;
  /** O dia relatado, `AAAA-MM-DD` — o prazo é contado até ele, e não até hoje. */
  date: string;
  report: DailyReport | undefined;
  canConfirm: boolean;
  canModify: boolean;
  isPending: boolean;
  /**
   * Emitido sem sinal (RN-624): o documento está fechado no aparelho, e o número ainda não
   * existe — quem o dá é o servidor, quando a emissão subir. Sem número, não há PDF.
   */
  emissionPending?: boolean;
  isGeneratingPdf: boolean;
  onClose: () => void;
  onRevise: () => void;
  onDownloadPdf: () => void;
}) {
  const prazo =
    project === undefined
      ? undefined
      : projectTerm({ startDate: project.startDate, endDate: project.endDate, date });

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <p className="text-base font-semibold">
              {report === undefined
                ? 'RDO (ainda não iniciado)'
                : emissionPending && report.number === undefined
                  ? 'RDO (o número sai quando a emissão subir)'
                  : describeDailyReport(report)}
            </p>
            <p className="truncate text-sm">{project?.name ?? 'Obra'}</p>
            {project?.client !== undefined && (
              <p className="truncate text-sm text-(--color-content-muted)">{project.client}</p>
            )}
            {project?.address !== undefined && (
              <p className="truncate text-sm text-(--color-content-muted)">{project.address}</p>
            )}
            <p className="text-sm text-(--color-content-muted)">
              Responsável técnico:{' '}
              {project?.technicalManager ?? (
                // Dizer que falta é melhor do que uma linha em branco: o nome sai no
                // documento que vai para fora, e quem administra a obra é quem o preenche.
                <span className="italic">não informado no cadastro da obra</span>
              )}
            </p>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-2">
            {report !== undefined &&
              (emissionPending ? (
                <Badge tone="info" icon={<CloudUpload aria-hidden="true" />}>
                  Emissão pendente
                </Badge>
              ) : (
                <Badge tone={TOM[report.status]}>{DAILY_REPORT_STATUS_LABEL[report.status]}</Badge>
              ))}
            <div className="flex flex-wrap justify-end gap-2">
              <AcaoDoDocumento
                report={report}
                canConfirm={canConfirm}
                canModify={canModify}
                isPending={isPending}
                onClose={onClose}
                onRevise={onRevise}
              />

              {report !== undefined && isRenderable(report) && !emissionPending && (
                <Button variant="secondary" disabled={isGeneratingPdf} onClick={onDownloadPdf}>
                  {isGeneratingPdf ? (
                    <Loader2 className="animate-spin" aria-hidden="true" />
                  ) : (
                    <Download aria-hidden="true" />
                  )}
                  {isGeneratingPdf ? 'Gerando…' : 'Baixar o PDF'}
                </Button>
              )}
            </div>

            {report?.status === 'DRAFT' && canConfirm && (
              <p className="max-w-56 text-right text-xs text-(--color-content-muted)">
                O PDF sai do documento emitido: é a emissão que dá o número.
              </p>
            )}
            {emissionPending && (
              <p className="max-w-56 text-right text-xs text-(--color-content-muted)">
                Emitido no aparelho. O número e o PDF saem quando o sinal voltar e a emissão subir.
              </p>
            )}
          </div>
        </div>

        {report?.status === 'OUTDATED' && (
          <p className="flex items-start gap-2 rounded-md bg-(--color-surface-muted) p-3 text-sm">
            <FileWarning
              className="mt-0.5 size-4 shrink-0 text-(--color-state-warn)"
              aria-hidden="true"
            />
            <span>
              Uma conferência deste dia foi reaberta depois que o RDO nº{' '}
              {String(report.number ?? 0)} saiu. O que foi entregue continua valendo; emitir de novo
              gera a revisão {String(report.revision + 1)}.
            </span>
          </p>
        )}

        {prazo !== undefined && (
          <dl className="flex flex-wrap gap-x-8 gap-y-2 border-t border-(--color-border-subtle) pt-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-(--color-content-muted)">
                Prazo
              </dt>
              <dd className="tabular font-medium">{dias(prazo.totalDays)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-(--color-content-muted)">
                Tempo decorrido
              </dt>
              <dd className="tabular font-medium">{dias(prazo.elapsedDays)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-(--color-content-muted)">
                Saldo
              </dt>
              {/* Saldo negativo é a obra passada do término previsto, e ele é dito com
                  todas as letras: apará-lo em zero apagaria o fato. */}
              <dd
                className={
                  prazo.remainingDays < 0
                    ? 'tabular font-medium text-(--color-state-danger)'
                    : 'tabular font-medium'
                }
              >
                {dias(prazo.remainingDays)}
              </dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function AcaoDoDocumento({
  report,
  canConfirm,
  canModify,
  isPending,
  onClose,
  onRevise,
}: {
  report: DailyReport | undefined;
  canConfirm: boolean;
  canModify: boolean;
  isPending: boolean;
  onClose: () => void;
  onRevise: () => void;
}) {
  const girando = isPending ? <Loader2 className="animate-spin" aria-hidden="true" /> : undefined;

  /*
   * Sem documento, nenhum botão.
   *
   * Havia um "Iniciar o relatório do dia" aqui, e o usuário o leu como cerimônia — com
   * razão: quem abre a página está nela para relatar o dia, e apertar "iniciar" não
   * acrescenta decisão nenhuma. O rascunho nasce do primeiro gesto que precisa dele: o
   * tempo, o efetivo, a tarefa, o trabalho lançado e, desde a Fase 22, a foto.
   *
   * O que se perde é pequeno e conhecido: o equipamento herdado do último RDO (ADR-123) só
   * aparece depois dessa primeira gravação, em vez de já estar lá ao abrir a página.
   */
  if (report === undefined) return null;

  if (report.status === 'CLOSED') {
    // Desfazer o que já foi entregue é ato de outra ordem que registrá-lo — é a mesma
    // razão pela qual reabrir conferência pede `labor.modify` (RN-106).
    if (!canModify) return null;
    return (
      <Button variant="secondary" disabled={isPending} onClick={onRevise}>
        {girando ?? <FileWarning aria-hidden="true" />}
        Abrir revisão
      </Button>
    );
  }

  if (!canConfirm) return null;
  return (
    <Button variant="primary" disabled={isPending} onClick={onClose}>
      {girando ?? <FileCheck2 aria-hidden="true" />}
      {report.status === 'OUTDATED'
        ? `Emitir a revisão ${String(report.revision + 1)}`
        : 'Emitir o RDO'}
    </Button>
  );
}
